import React, { useState, useEffect } from "react";
import { Paper, List, ListItem, ListItemText, Card, CardContent, TextField, LinearProgress } from "@mui/material";
import { Checkbox, ListItemButton, ListItemIcon, FormControlLabel, CircularProgress, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { telemetryService } from "../../services/TelemetryService";
import { deviceService } from "../../services/DeviceService";
import { Button } from "react-admin";

const useStyles = makeStyles((theme) => ({
    paperContainer: {
        boxShadow: theme.shadows[24],
        p: 2,
        height: "300px",
        overflowY: "auto",
    },
    listContainer: {
        width: "100%",
    },
    listItemContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    listItemTextContainer: {
        width: "80%",
        display: "flex",
        justifyContent: "flex-start",
        paddingLeft: theme.spacing(2),
    },
    secondaryText: {
        textAlign: "center",
        width: "50%",
        padding: 10,
    },
    errorText: {
        color: "red",
    },
}));

const FirmwareProvisionPlatform = () => {
    const classes = useStyles();
    const [isLoading, setLoading] = useState(false);
    const [requestLoading, setRequestLoading] = useState(false);
    const [selectionError, setSelectionError] = useState("");
    const [deviceData, setDeviceData] = useState([]);
    const [firmwareData, setFirmwareData] = useState([]);
    const [provisionLog, setProvisionLog] = useState(null);
    const [checkedFirmware, setCheckedFirmware] = useState([]);
    const [checkedDevices, setCheckedDevices] = useState([]);
    const [checkedList, setCheckedList] = useState([]);
    const [edgeMessageId, setEdgeMessageId] = useState(0);
    const [edgeMessage, setEdgeMessage] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const temp = await telemetryService.get("/getDevicesByPriority");
                const temp2 = await deviceService.getFirmwareVersions();
                setFirmwareData(temp2);
                setDeviceData(temp);
            } catch (error) {
                setSelectionError(`Error with fetching device data`);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (isLoading) {
        return (
            <div style={{ paddingBottom: "1rem" }}>
                <LinearProgress />
            </div>
        );
    }

    const handleEdgeMessageRequest = async () => {
        try {
            const edgeMessage = await telemetryService.get(`/firmware_provision_status/${edgeMessageId}`);
            setEdgeMessage(edgeMessage[0]);
        } catch (error) {
            console.log(error);
        }
    };

    const handleProvisionRequest = async () => {
        if (!checkedDevices.length || checkedFirmware === null) {
            setSelectionError("Please select device IDs and firmware before proceeding");
            return;
        }
        setRequestLoading(true);

        try {
            const ids = checkedDevices.join(",");
            const firmware = firmwareData[checkedFirmware];
            const logs = await deviceService.provision(ids, firmware);
            setProvisionLog(logs);
        } catch (error) {
            setSelectionError(`Error while requesting provision`);
        } finally {
            setRequestLoading(false);
        }
    };

    function EdgeMessageRender({ data }) {
        const entries = Object.entries(data);

        return (
            <CardContent>
                <Paper className={classes.paperContainer}>
                    <List className={classes.listContainer}>
                        {entries.map(([key, value]) => (
                            <ListItem key={key}>
                                <strong>{key}: </strong>
                                {value}
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </CardContent>
        );
    }

    function LogList({ logs }) {
        const entries = Object.entries(logs);
        return (
            <CardContent>
                <Paper className={classes.paperContainer}>
                    <List className={classes.listContainer}>
                        {entries.map(([deviceId, logEntries]) => (
                            <div key={deviceId}>
                                <h3>Device ID: {deviceId}</h3>
                                {logEntries.map((logEntry, index) => (
                                    <ListItem key={index}>{logEntry}</ListItem>
                                ))}
                            </div>
                        ))}
                    </List>
                </Paper>
            </CardContent>
        );
    }

    const DeviceList = ({ data, priorityGroup }) => {
        /**
         * Toggles the checkbox of a List alongside all the devices in the list
         */
        const handleListToggle = (event) => () => {
            let newCheckedList = [];
            let newCheckedDevices = [];
            const index = checkedList.indexOf(event);
            if (index === -1) {
                newCheckedList.push(priorityGroup);
                deviceData[priorityGroup].device_meta_data.map((device) => {
                    newCheckedDevices.push(device.edge_device_id);
                });
            }

            setCheckedDevices(newCheckedDevices);
            setCheckedList(newCheckedList);
        };

        /**
         * Toggles individual checkboxes of a device
         */
        const handleSingleToggle = (event) => () => {
            const currentIndex = checkedDevices.indexOf(event);
            const newCheckedDevices = [...checkedDevices];

            if (currentIndex === -1) {
                newCheckedDevices.push(event);
            } else {
                newCheckedDevices.splice(currentIndex, 1);
            }
            setCheckedList([]);
            setCheckedDevices(newCheckedDevices);
        };

        return (
            <Grid item xs={12}>
                <Card>
                    <FormControlLabel
                        control={
                            <Checkbox checked={checkedList.indexOf(priorityGroup) !== -1} tabIndex={-1} disableRipple />
                        }
                        onClick={handleListToggle(priorityGroup)}
                        style={{ paddingLeft: 2 }}
                        label={`Priority Group: ${priorityGroup === 0 ? "No Priority" : priorityGroup}`}
                    />

                    <CardContent>
                        <Paper className={classes.paperContainer}>
                            <List className={classes.listContainer}>
                                {data.map((device, index) => (
                                    <ListItem key={index} className={classes.listItemContainer}>
                                        <ListItemText
                                            primary={`Serial No: ${device.serial_no}`}
                                            secondary={`Edge Device ID: ${device.edge_device_id} 
                                                Config Version No: ${device.version_no} 
                                                Status: ${device.status}`}
                                            className={classes.listItemTextContainer}
                                            secondaryTypographyProps={{ className: classes.secondaryText }}
                                        />
                                        <ListItemButton
                                            role={undefined}
                                            dense
                                            onClick={handleSingleToggle(device.edge_device_id)}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={checkedDevices.indexOf(device.edge_device_id) !== -1}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    const ProvisionDeviceLists = () => {
        return (
            <Grid container spacing={2}>
                {deviceData.map((group, index) => (
                    <DeviceList key={index} data={group.device_meta_data} priorityGroup={group.priority_group} />
                ))}
            </Grid>
        );
    };

    const FirmwareList = () => {
        const extractFirmwareVersion = (fileName) => {
            const regex = /lux_firmware\/(CG-MINI_(REL|DEV)_\d+_\d+)/;
            const match = fileName.match(regex);
            return match ? match[1] : fileName;
        };

        const handleToggle = (value) => () => {
            const currentIndex = checkedFirmware.indexOf(value);
            const newChecked = [];

            if (currentIndex === -1) {
                newChecked.push(value);
            }
            setCheckedFirmware(newChecked);
        };

        return (
            <Grid item xs={12}>
                <Paper className={classes.paperContainer}>
                    <List className={classes.listContainer}>
                        {firmwareData.map((firm, index) => (
                            <ListItem key={index} className={classes.listItemContainer}>
                                <ListItemText
                                    primary={`${extractFirmwareVersion(firm)}`}
                                    className={classes.listItemTextContainer}
                                ></ListItemText>
                                <ListItemButton role={undefined} onClick={handleToggle(index)} dense>
                                    <ListItemIcon>
                                        <Checkbox
                                            checked={checkedFirmware.indexOf(index) !== -1}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                    </ListItemIcon>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Grid>
        );
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <h1>Device Metadata Lists</h1>
                <ProvisionDeviceLists />
            </Grid>
            <Grid item xs={6}>
                <Card></Card>
                <h1>Firmware Versions</h1>
                <FirmwareList />
                <Button
                    onClick={handleProvisionRequest}
                    variant="contained"
                    size="large"
                    disabled={requestLoading}
                    label="PROVISION"
                    startIcon={requestLoading ? <CircularProgress size={24} color="inherit" /> : null}
                />
                <h1>Firmware Provision Status</h1>
                <Card>
                    {provisionLog ? <LogList logs={provisionLog} /> : " "}
                    {selectionError && <p className={classes.errorText}>{selectionError}</p>}
                </Card>
                <h1>Device Edge Message status</h1>
                <Card>
                    <TextField
                        required
                        label="Edge Device Id"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                            setEdgeMessageId(e.target.value);
                        }}
                        defaultValue={edgeMessageId}
                    />
                    <Button onClick={handleEdgeMessageRequest} variant="contained" size="large" label="SEARCH" />
                    {edgeMessage ? <EdgeMessageRender data={edgeMessage} /> : " "}
                </Card>
            </Grid>
        </Grid>
    );
};

export default FirmwareProvisionPlatform;
